import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Share2, Lock, Sparkles, Check, LogOut, Bell, ShoppingBag, Heart, Users, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AuthContext } from '@/App';

const LOGO_URL = "/logo.png";

const HomePage = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Header with Dashboard & Logout */}
      {user && (
        <div className="absolute top-4 right-4 z-20 flex gap-2">
          <Button
            onClick={() => navigate('/dashboard')}
            className="gap-2 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-semibold"
            data-testid="home-dashboard-button"
          >
            <Calendar className="w-4 h-4" />
            My Dashboard
          </Button>
          <Button
            onClick={logout}
            variant="outline"
            className="gap-2 bg-white/95 backdrop-blur hover:bg-white border-2 border-gray-300"
            data-testid="home-logout-button"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>
      )}

      {/* Hero Section */}
      <div className="relative z-10 flex flex-col items-center justify-center px-4 py-20 text-center">
        <div className="max-w-5xl w-full space-y-8">
          {/* Logo */}
          <div className="flex justify-center mb-6 float-animation">
            <img 
              src={LOGO_URL}
              alt="My Wishlist Logo" 
              className="h-32 sm:h-40 w-auto drop-shadow-2xl"
              data-testid="home-logo"
            />
          </div>

          {/* Headline */}
          <div className="space-y-6">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-tight" style={{color: '#ffffff'}} data-testid="hero-headline">
              Your Wishlist,
              <br />
              <span className="text-yellow-300 drop-shadow-xl">
                Their Surprise
              </span>
            </h1>
            <p className="text-lg sm:text-xl font-medium max-w-3xl mx-auto leading-relaxed" style={{color: '#ffffff'}} data-testid="hero-subheadline">
              Create a wishlist with gifts you love.
              <br />
              Share it with friends and family.
              <br />
              <strong>They pick what to gift you — but you won't know who chose what until the big day.</strong>
            </p>
          </div>

          {/* 1-Stop Solution Info */}
          <div className="mb-8 px-4">
            <div className="glass-card px-6 sm:px-10 py-4 sm:py-5 rounded-2xl shadow-xl inline-block">
              <p className="text-gray-800 font-semibold text-base sm:text-lg text-center">
                🎉 Your 1-Stop Event Solution: Invites + RSVP + Wishlists 🎉
              </p>
            </div>
          </div>

          {/* CTA Button */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/dashboard')}
              size="lg"
              className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white px-12 py-8 rounded-full text-xl font-bold shadow-2xl transition-all transform hover:scale-105"
              data-testid="start-wishlist-button"
            >
              <Calendar className="w-6 h-6 mr-2" />
              Go to My Dashboard
            </Button>
          </div>

          {/* Quick Actions */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="glass-card p-6 text-center hover:scale-105 transition-transform">
              <div className="text-4xl mb-3">🎂</div>
              <h3 className="font-bold text-gray-800 mb-2">Create Events</h3>
              <p className="text-sm text-gray-600">Unlimited events for all occasions</p>
            </div>
            <div className="glass-card p-6 text-center hover:scale-105 transition-transform">
              <div className="text-4xl mb-3">🎁</div>
              <h3 className="font-bold text-gray-800 mb-2">Build Wishlists</h3>
              <p className="text-sm text-gray-600">Add gifts from any website</p>
            </div>
            <div className="glass-card p-6 text-center hover:scale-105 transition-transform">
              <div className="text-4xl mb-3">💌</div>
              <h3 className="font-bold text-gray-800 mb-2">Send Invitations</h3>
              <p className="text-sm text-gray-600">Beautiful themed invites</p>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="relative z-10 py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-extrabold text-center mb-4" style={{color: '#ffffff'}} data-testid="how-it-works-heading">
            How My Wishlist Works
          </h2>
          <p className="text-center mb-16 text-lg" style={{color: '#ffffff'}}>Simple as 1, 2, 3, 4, 5!</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              {
                icon: <Calendar className="w-10 h-10 text-white" />,
                number: '1',
                title: 'Create Event & Wishlist',
                desc: 'Create your event (birthday, wedding, etc.) and build a wishlist. You can reuse wishlists across multiple events!'
              },
              {
                icon: <Gift className="w-10 h-10 text-white" />,
                number: '2',
                title: 'Add Gifts You Want',
                desc: 'Paste product URLs from Amazon, Myntra, Nykaa, FirstCry, etc. We auto-fetch the title, photo & price.'
              },
              {
                icon: <Share2 className="w-10 h-10 text-white" />,
                number: '3',
                title: 'Share Invitation & Wishlist',
                desc: 'Send beautiful themed invitations and share your wishlist link via WhatsApp separately or together.'
              },
              {
                icon: <Lock className="w-10 h-10 text-white" />,
                number: '4',
                title: 'Loved Ones Pick Gifts',
                desc: 'Guests select gifts and can request your delivery address. Smart protection prevents gift hoarding!'
              },
              {
                icon: <Sparkles className="w-10 h-10 text-white" />,
                number: '5',
                title: 'Enjoy the Surprise!',
                desc: 'You receive gifts from your list — but the giver is a fun mystery until the moment you open it.'
              }
            ].map((step, index) => (
              <div key={index} className="glass-card p-8 text-center space-y-4 hover:scale-105 transition-transform" data-testid={`step-${index+1}`}>
                <div className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto shadow-xl" style={{background: '#FF3E96'}}>
                  {step.icon}
                </div>
                <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto" style={{background: '#2563EB'}}>
                  <span className="text-white font-bold text-xl">{step.number}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-800">{step.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Why People Love It */}
      <div className="relative z-10 py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-extrabold text-center mb-4" style={{color: '#ffffff'}}>
            Thoughtful. Organized. Fun.
          </h2>
          <p className="text-center mb-16 text-lg" style={{color: '#ffffff'}}>Why People Love My Wishlist</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: '💖', title: 'You get useful gifts', desc: 'No more random or repeat presents.' },
              { icon: '🤫', title: 'Surprise in the sender', desc: 'You know the gift. Not the gifter.' },
              { icon: '📱', title: 'No app download for guests', desc: 'Invitees open your wishlist directly.' },
              { icon: '🛍️', title: 'Works with any shopping site', desc: 'Add items from any store in India.' },
              { icon: '🔔', title: 'Auto reminders', desc: 'Invitees get reminders: 7 days, 3 days, 1 day, day of event.' },
              { icon: '🔒', title: 'Smart locking', desc: 'Once a gift is reserved, others can\'t select it.' }
            ].map((feature, index) => (
              <div key={index} className="glass-card p-6 hover:scale-105 transition-transform">
                <div className="text-5xl mb-4">{feature.icon}</div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* For Every Celebration */}
      <div className="relative z-10 py-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-extrabold mb-6" style={{color: '#ffffff'}}>
            For Every Celebration
          </h2>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {['🎂 Birthdays', '💑 Anniversaries', '👶 Baby Showers', '🏠 Housewarmings', '🎊 Festivals', '💍 Engagements', '🎓 Graduations'].map((event, index) => (
              <div key={index} className="glass-card px-6 py-3 text-lg font-semibold text-gray-800 hover:scale-105 transition-transform">
                {event}
              </div>
            ))}
          </div>
          <p className="text-xl font-medium" style={{color: '#ffffff'}}>
            Where gifting feels personal, joyful, and surprise-filled. ✨
          </p>
        </div>
      </div>

      {/* Testimonials */}
      <div className="relative z-10 py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-extrabold text-center mb-16" style={{color: '#ffffff'}}>
            What Our Users Say
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { text: 'The best birthday ever! I loved every gift and had a blast guessing who got me what.', author: 'Kavya' },
              { text: 'The app solved all my gifting confusion.', author: 'Rohan' },
              { text: 'Made our baby shower so organized.', author: 'Ritu & Arjun' }
            ].map((testimonial, index) => (
              <div key={index} className="glass-card p-8 space-y-4 hover:scale-105 transition-transform border-l-4" style={{borderColor: '#FF3E96'}}>
                <div className="text-3xl">⭐⭐⭐⭐⭐</div>
                <p className="text-gray-800 text-lg leading-relaxed italic">"{testimonial.text}"</p>
                <p className="text-gray-600 font-semibold">— {testimonial.author}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="relative z-10 py-20 px-4 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card p-12 space-y-8">
            <h2 className="text-4xl sm:text-5xl font-extrabold text-gray-800 leading-tight">
              Ready to create your perfect wishlist?
            </h2>
            <Button
              onClick={() => navigate('/dashboard')}
              size="lg"
              style={{background: 'linear-gradient(135deg, #FF3E96 0%, #FF1744 100%)'}}
              className="text-white px-16 py-8 rounded-full text-2xl font-bold shadow-2xl hover:opacity-90 transition-all transform hover:scale-110"
            >
              Start My Wishlist
            </Button>
            <Button
              onClick={() => navigate('/about')}
              variant="outline"
              size="lg"
              className="ml-4 px-12 py-8 rounded-full text-xl font-semibold border-2"
              style={{borderColor: '#2563EB', color: '#2563EB'}}
            >
              Learn More
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 py-12 px-4 bg-white/95 backdrop-blur border-t-4" style={{borderColor: '#FF3E96'}}>
        <div className="max-w-6xl mx-auto text-center space-y-6">
          <div className="text-4xl mb-4">🎁</div>
          <div className="flex flex-wrap justify-center gap-8 text-base text-gray-700 font-medium">
            <a href="/about" className="hover:text-pink-600 transition-colors">About</a>
            <a href="/faqs" className="hover:text-pink-600 transition-colors">FAQs</a>
            <a href="/support" className="hover:text-pink-600 transition-colors">Support</a>
            <a href="/privacy" className="hover:text-pink-600 transition-colors">Privacy Policy</a>
            <a href="/terms" className="hover:text-pink-600 transition-colors">Terms</a>
          </div>
          <div className="h-px bg-gradient-to-r from-transparent via-pink-500 to-transparent"></div>
          <p className="text-gray-700 text-base font-semibold">
            © 2025 My Wishlists — <span style={{color: '#FF3E96'}}>Know the Gifts, Not the Gifter</span> 💝
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;